import { useEffect, useState } from "react";
import { NavButtons } from "../components/NavButtons";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Bell, X } from "@phosphor-icons/react";
import PageLoader from "@/components/PageLoader";

const SEV_COLOR = {
  danger: "#dc2626", warning: "#d97706", info: "var(--text-dim)",
};
const SEV_LABEL = { danger: "Overdue", warning: "Attention", info: "Info" };

export default function ClientNotificationsPage() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (_e) {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    setItems((items || []).filter(i => i.id !== id));
    try { await api.delete(`/notifications/${encodeURIComponent(id)}`); }
    catch (_e) { toast.error("Failed"); load(); }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    setItems([]);
    try { await api.delete(`/notifications`); }
    catch (_e) { toast.error("Failed"); load(); }
  };

  if (items === null) {
    return (
      <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
        <PageLoader label="Loading notifications…" testid="client-notif-loader" />
      </div>
    );
  }

  const filtered = filter === "all" ? items : items.filter(i => (i.severity || "info") === filter);
  const counts = {
    all: items.length,
    danger: items.filter(i => i.severity === "danger").length,
    warning: items.filter(i => i.severity === "warning").length,
    info: items.filter(i => (i.severity || "info") === "info").length,
  };

  return (
    <div className="min-h-screen" data-testid="client-notifications-page"
      style={{ background: "var(--bg)", color: "var(--text)" }}>
      <header className="flex items-center justify-between gap-3 px-4 md:px-6 py-4 md:py-6 border-b"
        style={{ borderColor: "var(--hair)" }}>
        <div className="min-w-0">
          <NavButtons home="/dashboard" testid="back-to-dashboard" />
          <h1 className="title-xl text-2xl md:text-4xl mt-1 truncate">Notifications.</h1>
          <p className="label mt-1">Everything that needs your attention, in one place.</p>
        </div>
        <Bell size={28} weight="bold" />
      </header>

      <section className="p-4 md:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {[
            ["all", "All"],
            ["danger", "Overdue"],
            ["warning", "Attention"],
            ["info", "Info"],
          ].map(([k, label]) => (
            <button key={k}
              data-testid={`client-notif-filter-${k}`}
              onClick={() => setFilter(k)}
              className="btn-ghost text-xs"
              style={{
                background: filter === k ? "var(--text)" : "transparent",
                color: filter === k ? "var(--bg)" : "var(--text)",
                border: "1px solid var(--hair)",
              }}>
              {label} · {counts[k]}
            </button>
          ))}
          {items.length > 0 && (
            <button onClick={clearAll}
              data-testid="client-notif-page-clear-all"
              className="btn-ghost text-xs ml-auto"
              style={{ color: "var(--text-dim)" }}>
              Clear all
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="panel p-12 text-center">
            <p className="title-xl text-2xl">You're all caught up.</p>
            <p className="label mt-2">Nothing matches this filter right now.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(n => (
              <div key={n.id} data-testid={`client-notif-page-row-${n.id}`}
                className="panel p-4 flex items-start gap-3">
                <span className="mt-1 shrink-0 w-2.5 h-2.5 rounded-full"
                  style={{ background: SEV_COLOR[n.severity] || SEV_COLOR.info }} />
                <Link to={n.link || "#"} className="flex-1 min-w-0 hover:opacity-80">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium" style={{ overflowWrap: "anywhere" }}>{n.title}</p>
                    <span className="label text-[10px] px-2 py-0.5 rounded-full"
                      style={{
                        background: (SEV_COLOR[n.severity] || SEV_COLOR.info) + "22",
                        color: SEV_COLOR[n.severity] || SEV_COLOR.info,
                        fontWeight: 700,
                      }}>
                      {SEV_LABEL[n.severity] || "Info"}
                    </span>
                  </div>
                  {n.subtitle && (
                    <p className="text-xs mt-1"
                      style={{ color: "var(--text-dim)", overflowWrap: "anywhere" }}>
                      {n.subtitle}
                    </p>
                  )}
                </Link>
                <button onClick={() => dismiss(n.id)}
                  className="text-xs p-2 rounded hover:opacity-70"
                  style={{ background: "transparent", border: "none", color: "var(--text-dim)" }}
                  data-testid={`client-notif-page-dismiss-${n.id}`}
                  title="Dismiss">
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
